import React, { FC, useCallback, useEffect, useState } from 'react';
import { useTranslate } from '@translations/useTranslate';
import { useDocumentTitle } from '@hooks/index';
import { VisitRequestOtpForm } from '@ui/components/organisms/VisitRequestOtpForm';
import { VisitRequestForm } from '@ui/components/organisms/VisitRequestForm';
import { RouteComponentProps } from 'react-router-dom';
import { VisitRequestStatus } from '@ui/components/organisms/VisitRequestStatus';
import { useVerifyRequestStatus } from '@services/VisitRequest';
import { Translations } from '@translations/translationsTypes';
import { VisitRequestVIPForm } from '@ui/components/organisms/VisitRequestForm/components/VisitRequestVIPForm';
import { VisitRequestStandAloneForm } from '@ui/components/organisms/VisitRequestForm/components/VisitRequestStandAloneForm';
import { IMappedVisitRequestResponse } from '@interfaces/VisitRequestLookups.interface';

interface VisitRequestRouteParams {
  requestId: string;
}

interface VisitRequestProps extends RouteComponentProps<VisitRequestRouteParams> {
  param: string;
}
export const VisitRequest: FC<VisitRequestProps> = (props: VisitRequestProps) => {
  const { t, setLanguage } = useTranslate();
  useDocumentTitle(t('page.title.visit'));

  useEffect(() => {
    setLanguage(Translations.en);
  }, [setLanguage]);

  const [currentPage, setCurrentPage] = React.useState(0);
  const [requestId] = useState(props?.match?.params?.requestId ?? '');
  const [submittedRequestId, setSubmittedRequestId] = useState<string>('');
  const { mutateAsync: verifyRequestStatusMutateAsync } = useVerifyRequestStatus();
  const [formFillData, setFormFillData] = useState<IMappedVisitRequestResponse>();
  const changeCurrentPage = React.useCallback((page) => {
    setCurrentPage(page);
  }, []);
  const [isVIPForm, setIsVIPForm] = React.useState(false);
  const verifyStatusOfRequest = useCallback(() => {
    verifyRequestStatusMutateAsync(requestId, {
      onError: () => {
        changeCurrentPage(4);
      },
    })
      .then((result) => {
        const statusText = result as string;
        if (
          statusText.toLowerCase() === 'invitation_expired' ||
          statusText.toLowerCase() === 'request_submitted' ||
          statusText.toLowerCase() === 'rejected' ||
          statusText.toLowerCase() === 'closed'
        ) {
          changeCurrentPage(4);
        } else {
          changeCurrentPage(1);
        }
      })
      .catch(() => {
        //Ignore the exception
      });
  }, [changeCurrentPage, requestId, verifyRequestStatusMutateAsync]);

  useEffect(() => {
    verifyStatusOfRequest();
  }, [verifyStatusOfRequest]);

  return (
    <>
      <h2 className="w-100 text-center fw-bold pt-2 pb-3">{t('vr.form.title')}</h2>

      {currentPage === 1 && (
        <VisitRequestOtpForm
          requestId={requestId}
          onSubmitOtp={(data) => {
            setIsVIPForm(data.isVVIP);
            setFormFillData(data);
            changeCurrentPage(2);
          }}
        />
      )}

      {formFillData && (
        <>
          {currentPage === 2 && isVIPForm && (
            <VisitRequestVIPForm
              requestId={requestId}
              requestData={formFillData}
              onRequestApprove={() => {
                changeCurrentPage(3);
              }}
              onRequestSuccess={() => {
                changeCurrentPage(6);
              }}
              onRequestFailed={() => {
                changeCurrentPage(4);
              }}
            />
          )}
          {currentPage === 2 && !isVIPForm && (
            <VisitRequestStandAloneForm
              requestId={requestId}
              requestData={formFillData}
              onRequestApprove={() => {
                changeCurrentPage(3);
              }}
              onRequestSuccess={() => {
                changeCurrentPage(6);
              }}
              onRequestFailed={() => {
                changeCurrentPage(4);
              }}
            />
          )}
          {currentPage === 3 && (
            <VisitRequestForm
              requestId={requestId}
              requestData={formFillData}
              isVVIP={isVIPForm}
              onRequestSuccess={(id) => {
                changeCurrentPage(5);
                setSubmittedRequestId(id);
              }}
              onRequestFailed={() => {
                changeCurrentPage(4);
              }}
            />
          )}
        </>
      )}
      {currentPage === 4 && <VisitRequestStatus isSuccess={false} requestNumber={requestId} />}
      {currentPage === 5 && submittedRequestId && (
        <VisitRequestStatus isSuccess={true} requestNumber={submittedRequestId} />
      )}
      {currentPage === 6 && <VisitRequestStatus isRejected={true} requestNumber={requestId} />}
    </>
  );
};
